import { useEffect, useMemo, useRef, useState } from 'react';
import dagre from 'dagre';
import { v4 as uuidv4 } from 'uuid';

import {
  calculateDagreLayout,
  dagreCacheKey,
  GlobalCacheInput,
} from '../dag/calculateDagreLayout';
import { GetBoundingBoxFn, NodeType, useGetNodeBoundingBox } from '../dag/dagConstants';
import { DagStats, DbtDagNode } from '../dag/DagInterfaces';
import {
  DagreResult,
  InstrumentedDagreResult,
  PreLayoutEdge,
  PreLayoutNode,
} from '../dag/DagreTypes';
import { DagGraph, generateDagGraph } from '../dag/internalDag';
import { DagMap, DagreNode } from '../dag/InternalDagInterfaces';
import { useInternalDagContext } from '../providers/InternalDagProvider';
import { createLruCache } from './lruCache';

const MAX_CACHE_SIZE = 50;
/**
 * The Dagre Cache lives outside of react, to ensure we can
 * maximize the cache hit rate. This means if you unmount
 * the DAG, or render multiple DAGs, they can still
 * share the same cache.
 */
const dagreCache = createLruCache<DagreResult<any>>(MAX_CACHE_SIZE);

// Only used for testing
export const clearDagreCache = () => {
  dagreCache.clear();
};

export function isDagreLayoutCached<TDbtDagNode extends DbtDagNode>(
  dbtDagMap: DagMap<TDbtDagNode>,
  globalCacheInput: GlobalCacheInput,
): boolean {
  const cacheKey = dagreCacheKey(dbtDagMap, globalCacheInput);
  return dagreCache.has(cacheKey);
}

export async function getDagreDagLayout<TDbtDagNode extends DbtDagNode>(
  dbtDagMap: DagMap<TDbtDagNode>,
  defaultReactNodeType: NodeType,
  getNodeBoundingBox: GetBoundingBoxFn<TDbtDagNode>,
  globalCacheInput: GlobalCacheInput,
): Promise<DagreResult<TDbtDagNode>> {
  const cacheKey = dagreCacheKey(dbtDagMap, globalCacheInput);
  const dagreResult = await dagreCache.get(cacheKey, () =>
    generateDagreDagLayout(dbtDagMap, defaultReactNodeType, getNodeBoundingBox),
  );
  return dagreResult;
}

export const useDagreDagLayout = <TDbtDagNode extends DbtDagNode>(
  dbtDagMap: DagMap<TDbtDagNode>,
  forceRenderComplexDag: boolean,
  onDagLayoutStart:
    | ((
        stats: Omit<
          DagStats,
          'layoutDurationMillis' | 'renderStartTimestamp' | 'renderDurationMillis'
        >,
      ) => void)
    | undefined,
  onDagLayoutResult?: (
    result: DagreResult<TDbtDagNode>,
  ) => DagreResult<TDbtDagNode> | void,
) => {
  const [dagreResult, setDagreResult] = useState<DagreResult<TDbtDagNode> | null>(null);
  const [dagGraph, setDagGraph] = useState<DagGraph<TDbtDagNode>>();
  const [layoutDurationMillis, setLayoutDurationMillis] = useState<number | null>(null);
  const [endLayoutTimestamp, setEndLayoutTimestamp] = useState<number | null>(null);

  const requestIdRef = useRef<string>(uuidv4());
  requestIdRef.current = uuidv4();
  const [requestId, setRequestId] = useState(requestIdRef.current);

  const getNodeBoundingBox = useGetNodeBoundingBox();
  const {
    activeDbtCloudProject,
    nodeType: defaultNodeType,
    grain,
  } = useInternalDagContext();

  useEffect(() => {
    let isMounted = true;
    const unmount = () => {
      isMounted = false;
    };
    setDagreResult(null);
    setLayoutDurationMillis(null);
    setEndLayoutTimestamp(null);

    const requestId = requestIdRef.current;
    setRequestId(requestId);

    const dagGraph = generateDagGraph(dbtDagMap);
    setDagGraph(dagGraph);
    if (onDagLayoutStart) {
      const nodeIds = Object.keys(dagGraph);
      const edgeCount = nodeIds
        .map((nodeId) => dagGraph[nodeId].children.length)
        .reduce((acc, count) => acc + count, 0);
      onDagLayoutStart({
        edgeCount,
        nodeCount: nodeIds.length,
        forceRenderComplexDag,
        requestId,
      });
    }

    const startLayoutTimestamp = Date.now();
    getDagreDagLayout(dbtDagMap, defaultNodeType, getNodeBoundingBox, { grain }).then(
      (result) => {
        if (!isMounted) return unmount;
        const endLayoutTimestamp = Date.now();
        result.nodes = result.nodes.map((node) => {
          const nodeId = node.id;
          const newNode = {
            ...dbtDagMap[nodeId],
            id: nodeId,
          };
          const updatedData = transformToDagreNode(nodeId, newNode, defaultNodeType);
          return {
            ...node,
            ...updatedData,
            data: {
              id: nodeId,
              ...node.data,
              ...updatedData.data,
            },
          } as DagreNode<TDbtDagNode>;
        });

        const hydratedResult = onDagLayoutResult?.(result) ?? result;
        const hydratedDagGraph = generateDagGraph(
          hydratedResult.nodes.reduce(
            (acc, node) => {
              acc[node.id] = node;
              return acc;
            },
            {} as Record<string, DagreNode<TDbtDagNode>>,
          ),
        );
        setDagGraph(hydratedDagGraph);
        setDagreResult(hydratedResult);
        setLayoutDurationMillis(
          hydratedResult.wasCached ? null : endLayoutTimestamp - startLayoutTimestamp,
        );
        setEndLayoutTimestamp(endLayoutTimestamp);
      },
    );
    return unmount;
  }, [
    activeDbtCloudProject,
    dbtDagMap,
    defaultNodeType,
    forceRenderComplexDag,
    getNodeBoundingBox,
    grain,
    onDagLayoutResult,
    onDagLayoutStart,
  ]);

  return useMemo(() => {
    return {
      /**
       * The result of a DAG layout, the DAG layout can have stale data (any node data that isn't relevant for layout and size)
       */
      dagreResult: dagreResult
        ? ({
            ...dagreResult,
            /**
             * How long it took to layout the DAG. `null` if layout was cached.
             */
            layoutDurationMillis,
            endLayoutTimestamp,
            /**
             * The requestId which was supplied when the layout started.
             */
            dagRequestId: requestId,
          } satisfies InstrumentedDagreResult<TDbtDagNode>)
        : null,
      /**
       * A representation of the Graph useful for crawling along parents/children
       */
      dagGraph,
    };
  }, [dagGraph, dagreResult, endLayoutTimestamp, layoutDurationMillis, requestId]);
};

function generateNodesAndEdges<TDbtDagNode extends DbtDagNode>(
  dagMap: DagMap<TDbtDagNode>,
  defaultReactNodeType: NodeType,
  getNodeBoundingBox: GetBoundingBoxFn<TDbtDagNode>,
) {
  const inputNodes: PreLayoutNode<TDbtDagNode>[] = [];
  const inputEdges: PreLayoutEdge[] = [];
  const errorNodes = new Set<string>();

  Object.keys(dagMap).forEach((nodeId) => {
    const node = {
      ...dagMap[nodeId],
      id: nodeId,
    };
    const partialDagreNode = transformToDagreNode(nodeId, node, defaultReactNodeType);

    const {
      label,
      sublabel: _sublabel,
      resourceType: _resourceType,
      parents,
      data: _data,
      ...remainder
    } = node;

    if (!label || !nodeId) return;

    const isError = node.type === 'error';
    if (isError) {
      errorNodes.add(nodeId);
    }

    const { nodeWidth, nodeHeight, layoutWidth, layoutHeight } =
      getNodeBoundingBox(node);

    const dagreNode: DagreNode<TDbtDagNode> = {
      ...remainder,
      ...partialDagreNode,
      x: 0,
      y: 0,
      width: layoutWidth,
      height: layoutHeight,
      nodeWidth,
      nodeHeight,
    };
    inputNodes.push({ id: nodeId, dagreNode });
    parents.forEach((parentId) => {
      inputEdges.push({ startId: parentId, endId: nodeId });
    });
  });

  return { inputNodes, inputEdges };
}

function transformToDagreNode<TDbtDagNode extends DbtDagNode>(
  nodeId: string,
  node: TDbtDagNode,
  defaultReactNodeType: NodeType,
) {
  const isError = node.type === 'error';
  const dagreNode = {
    ...node,
    id: nodeId,
    type: isError ? 'error' : defaultReactNodeType,
  };
  return dagreNode;
}

function generateDagreDagLayout<TDbtDagNode extends DbtDagNode>(
  dbtDagMap: DagMap<TDbtDagNode>,
  defaultReactNodeType: NodeType,
  getNodeBoundingBox: GetBoundingBoxFn<TDbtDagNode>,
): DagreResult<TDbtDagNode> {
  const graph = new dagre.graphlib.Graph<{ data: TDbtDagNode }>();
  const graphConfig = {
    rankdir: 'LR',
    ranksep: 100,
    edgesep: 30,
    nodesep: 20,
    acyclicer: 'greedy',
    /** ul, ur, dl, dr */
    // align: 'ul',
    /** network-simplex, tight-tree or longest-path */
    // longest path should be faster
    ranker: 'longest-path',
  };
  // Configure the graph to be laid out
  graph.setGraph(graphConfig);
  // Default to assigning a new object as a label for each new edge.
  graph.setDefaultEdgeLabel(function () {
    return {};
  });

  const { inputNodes, inputEdges } = generateNodesAndEdges(
    dbtDagMap,
    defaultReactNodeType,
    getNodeBoundingBox,
  );
  return calculateDagreLayout({
    graphConfig,
    nodes: inputNodes,
    edges: inputEdges,
  });
}
